import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import SmartImage from './SmartImage';
import {
  Users,
  GraduationCap,
  Award,
  Shield,
  ArrowRight,
  Building2,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ICON_MAP = {
  users: Users,
  school: GraduationCap,
  award: Award,
  shield: Shield,
  building: Building2,
};

const PartnershipsSection = () => {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/landing/partnerships-config`);
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (error) {
      console.error('Failed to load partnerships config:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFileUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (typeof window !== 'undefined') {
      return `${window.location.origin}${url}`;
    }
    return `${BACKEND_URL}${url}`;
  };

  if (loading || !config || !config.show_on_homepage) {
    return null;
  }

  const title = config.homepage_title || 'Trusted by Schools & Organizations';
  const subtitle = config.homepage_subtitle || 'Building water safety through meaningful partnerships';
  const metrics = config.impact_metrics || [];
  const logos = config.logos || [];

  return (
    <section className="py-20 bg-slate-50" data-testid="partnerships-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20">
            Our Partners
          </Badge>
          <h2 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            {title}
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        {/* Metrics Strip */}
        {metrics.length > 0 && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-12">
            {metrics.slice(0, 4).map((metric) => {
              const IconComponent = ICON_MAP[metric.icon] || Users;
              return (
                <div
                  key={metric.id}
                  className="bg-white rounded-xl p-6 text-center shadow-sm border border-slate-100"
                >
                  <IconComponent className="w-8 h-8 text-primary mx-auto mb-3" />
                  <div className="text-2xl sm:text-3xl font-bold text-slate-900 mb-1">
                    {metric.value}
                  </div>
                  <div className="text-sm text-slate-600">{metric.label}</div>
                </div>
              );
            })}
          </div>
        )}

        {/* Partner Logos */}
        {logos.length > 0 && (
          <div className="mb-10">
            <div className="flex flex-wrap items-center justify-center gap-8">
              {logos.slice(0, 8).map((logo, idx) => (
                <div
                  key={logo.id || idx}
                  className="w-24 h-16 sm:w-32 sm:h-20 flex items-center justify-center grayscale hover:grayscale-0 transition-all opacity-70 hover:opacity-100"
                >
                  <SmartImage
                    src={getFileUrl(logo.url)}
                    alt={logo.alt || 'Partner logo'}
                    className="max-w-full max-h-full object-contain"
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="text-center">
          <Link to="/partner-with-us">
            <Button size="lg" className="bg-primary hover:bg-primary/90">
              Become a Partner
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
};

export default PartnershipsSection;
